import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  Paper,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminService } from '../../services/adminService';

const UsuarioRoles = () => {
  const navigate = useNavigate();
  const { usuarioId } = useParams();
  const [rolesDisponiveis, setRolesDisponiveis] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [rolesUsuario, setRolesUsuario] = useState([]);
  const [rolesSelecionadas, setRolesSelecionadas] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    carregarDados();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuarioId]);

  const carregarDados = async () => {
    setLoading(true);
    setError('');
    try {
      // Carregar todas as roles disponíveis
      const dadosRoles = await adminService.listarRoles(1, 100);
      
      // Carregar roles do usuário usando o endpoint recomendado (GET /api/users/{id}/roles)
      const dadosUsuario = await adminService.listarRolesUsuario(usuarioId);
      
      setRolesDisponiveis(dadosRoles.roles || []);
      
      // Marcar roles que o usuário já tem
      // O endpoint retorna roles no formato: { id, nome, descricao, identificador }
      const rolesAtuais = new Set(
        (dadosUsuario.roles || []).map(r => r.identificador || r.role?.identificador)
      );
      setRolesUsuario(dadosUsuario.roles || []);
      setRolesSelecionadas(rolesAtuais);
    } catch (err) {
      setError(err.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const toggleRole = (roleIdentificador) => {
    const novasSelecionadas = new Set(rolesSelecionadas);
    if (novasSelecionadas.has(roleIdentificador)) {
      novasSelecionadas.delete(roleIdentificador);
    } else {
      novasSelecionadas.add(roleIdentificador);
    }
    setRolesSelecionadas(novasSelecionadas);
  };

  const salvar = async () => {
    setSalvando(true);
    setError('');
    setSuccess('');
    
    try {
      // Converter Set para Array de UUIDs das roles selecionadas
      const rolesArray = Array.from(rolesSelecionadas);
      
      // Usar o endpoint recomendado (PUT /api/users/{id}/roles)
      // Este endpoint sincroniza automaticamente: adiciona o que falta e remove o que sobra
      await adminService.atualizarRolesUsuario(usuarioId, rolesArray);
      
      setSuccess('Roles atualizadas com sucesso!');
      setTimeout(() => {
        navigate('/admin/usuarios');
      }, 1500);
    } catch (err) {
      setError(err.message || 'Erro ao salvar roles');
    } finally {
      setSalvando(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/admin/usuarios')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          Gerenciar Roles do Usuário
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
          Selecione as roles para este usuário:
        </Typography>

        {rolesDisponiveis.length === 0 ? (
          <Typography color="text.secondary">
            Nenhuma role disponível
          </Typography>
        ) : (
          <List>
            {rolesDisponiveis.map((role, index) => (
              <React.Fragment key={role.id}>
                <ListItem>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={rolesSelecionadas.has(role.identificador)}
                        onChange={() => toggleRole(role.identificador)}
                        disabled={salvando}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {role.nome}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {role.descricao}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                {index < rolesDisponiveis.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
          <Button
            variant="contained"
            startIcon={salvando ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={salvar}
            disabled={salvando}
            sx={{ flex: 1 }}
          >
            {salvando ? 'Salvando...' : 'Salvar'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/admin/usuarios')}
            disabled={salvando}
          >
            Cancelar
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default UsuarioRoles;

